import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { flatten, type PropPart } from '../props/PropModels';
import { HexCellData } from './HexSurface';
import { DECK_HALF_WIDTH, bridgeSpans, type BridgeSpan } from './RoadSurface';

/**
 * Model mostu, stawiany tam, gdzie przeprawa jest prosta.
 *
 * **Most nie jest budynkiem i nie jest rekwizytem**, więc mieszka tutaj, przy
 * drogach, do których należy: nie stoi na polu wybranym przez gracza ani nie
 * wyrasta z zasobności złoża, tylko bierze się z tego, że droga przecięła
 * rzekę. `RoadSurface` mówi, gdzie i jak długie jest przęsło; ten plik mówi,
 * co na nim stanie.
 *
 * **Tylko przęsło o dwóch ramionach.** Skrzyżowanie z trzema i więcej zostaje
 * przy wstędze z pudełek — model ma jedną oś, a rozgałęziona przeprawa nie ma
 * żadnej, więc postawienie na niej trzech kopii dałoby trzy mosty wchodzące
 * jeden w drugi zamiast jednego węzła. Cena jest widoczna i warto ją znać: dwie
 * przeprawy obok siebie wyglądają wtedy inaczej, jeśli jedna jest rozwidleniem.
 *
 * # Dlaczego długość, a nie wysokość
 *
 * `flatten` normalizuje model do jedynki **wzrostu**, bo o drzewie i o budynku
 * pyta się „jak wysoki". O most pyta się „jak szerokie przęsło": rozpiętość
 * przychodzi z klasy szerokości rzeki i zmienia się prawie dwukrotnie — 0,25 do
 * 0,46 jednostki świata. Więc miarą jest tu dłuższa oś pozioma, a model jest
 * przy okazji obracany tak, żeby leżała ona wzdłuż **+X**: tak samo, jak leży
 * pokład z pudełek, zanim `plank` obróci go na kąt przęsła.
 *
 * # Skala jest niejednorodna i to jest decyzja
 *
 * Pokład zostaje tej samej grubości i szerokości nad strugą co nad szeroką
 * rzeką, a rozciąga się **tylko wzdłuż**. Odrzucone: skalowanie proporcjonalne,
 * przy którym most nad najszerszą rzeką byłby 2,2 raza wyższy niż nad strugą —
 * czytelne, ale zamienia jedną konstrukcję w rodzinę różnych; i jedna stała
 * skala, przy której most nad strugą wystaje poza wodę, a nad rzeką nie dosięga
 * brzegów, czyli przestaje składać się z drogą. Cena wybranego wariantu:
 * tekstura desek rozjeżdża się wzdłuż przęsła tym bardziej, im szersza rzeka.
 */

export interface BridgeModel {
  name: string;
  parts: PropPart[];
  /** Szerokość modelu, gdy jego długość wynosi jeden. */
  width: number;
}

/**
 * Wczytaj model mostu, albo nic.
 *
 * Brak pliku znaczy tyle, że przeprawy zostają wstęgą — dokładnie jak przed
 * jego przyjazdem, i dokładnie tak, jak rodzaj budynku bez modelu zostaje przy
 * swojej sylwetce. Świat bez modelu mostu jest grywalny, świat, który się nie
 * otworzył, nie jest.
 */
export async function loadBridgeModel(): Promise<BridgeModel | null> {
  try {
    const gltf = await new GLTFLoader().loadAsync('/models/bridge-1.glb');
    return alongLength(flatten('bridge-1', gltf.scene));
  } catch (error) {
    console.warn('model mostu nie wczytał się, przeprawy zostaną wstęgą:', error);
    return null;
  }
}

/**
 * Przemierz model wzdłuż, zamiast w górę.
 *
 * Wchodzi to, co wyszło z `flatten` — wypieczone transformacje węzłów, podstawa
 * na zerze, rzut wyśrodkowany — a wychodzi model długi na jedynkę. Obrót idzie
 * wokół osi Y przez zero, więc jedno i drugie zostaje na miejscu.
 */
function alongLength(model: ReturnType<typeof flatten>): BridgeModel {
  const bounds = new THREE.Box3();
  for (const part of model.parts) {
    part.geometry.computeBoundingBox();
    bounds.union(part.geometry.boundingBox!);
  }

  const alongX = bounds.max.x - bounds.min.x;
  const alongZ = bounds.max.z - bounds.min.z;
  const length = Math.max(alongX, alongZ, 1e-6);
  const width = Math.min(alongX, alongZ);

  for (const part of model.parts) {
    if (alongZ > alongX) part.geometry.rotateY(-Math.PI / 2);
    part.geometry.scale(1 / length, 1 / length, 1 / length);
    part.geometry.computeBoundingSphere();
  }

  return { name: model.name, parts: model.parts, width: width / length };
}

/**
 * Mosty jednego chunka jako instancje modelu, albo `null`, gdy nie ma tu ani
 * jednego prostego przęsła.
 *
 * Instancje, a nie kopie mesha, z tego samego powodu co przy głazach na
 * nieprzekraczalnych krawędziach: geometria i materiał należą do modelu, który
 * żyje dłużej niż chunk, więc chunk dokłada wyłącznie macierze.
 */
export function buildBridgeModels(
  members: HexCellData[],
  model: BridgeModel
): THREE.Group | null {
  const matrices: THREE.Matrix4[] = [];

  for (const span of bridgeSpans(members)) {
    if (!span.simple) continue;
    const matrix = spanMatrix(span, model);
    if (matrix) matrices.push(matrix);
  }

  if (matrices.length === 0) return null;
  return instanced(model, matrices);
}

/**
 * Gdzie i jak stoi model na jednym prostym przęśle — wspólne dla mostów
 * w świecie i dla podglądu stawianego mostu.
 */
export function spanMatrix(span: BridgeSpan, model: BridgeModel): THREE.Matrix4 | null {
  // Ile skalować w poprzek i w pionie, żeby pokład wyszedł tej szerokości, co
  // ten z pudełek.
  const across = (DECK_HALF_WIDTH * 2) / Math.max(model.width, 1e-6);
  const dx = span.b.x - span.a.x;
  const dz = span.b.z - span.a.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-6) return null;

  return (
      new THREE.Matrix4().compose(
        new THREE.Vector3(
          span.x + (span.a.x + span.b.x) / 2,
          // Podstawą na gruncie pola, nie na wysokości pokładu z pudełek: model
          // niesie własną konstrukcję pod deskami, a ta ma się o coś opierać.
          span.y,
          span.z + (span.a.z + span.b.z) / 2
        ),
        // Ten sam znak, co przy pokładzie z pudełek, i z tego samego powodu:
        // model leży wzdłuż +X, a `atan2(dz, dx)` mierzy kąt w układzie, w
        // którym obrót wokół Y idzie w drugą stronę.
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          -Math.atan2(dz, dx)
        ),
        new THREE.Vector3(length, across, across)
      )
  );
}

function instanced(model: BridgeModel, matrices: THREE.Matrix4[]): THREE.Group {
  const group = new THREE.Group();
  group.name = 'bridges';

  for (const part of model.parts) {
    const mesh = new THREE.InstancedMesh(part.geometry, part.material, matrices.length);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();

    group.add(mesh);
  }

  return group;
}
